import React, { useState, useEffect, useCallback } from "react";
import { Spinner } from "theme-ui";

import BasicButton from "components/forms/BasicButton";
import Checkbox from "components/forms/Checkbox";

import { checkPolicyAcceptance, acceptPolicy, getPolicyDownloadUrl } from "api/Policy";
import { POLICY_CONFIG, POLICY_REQUIRED_ACCESS_LEVELS, POLICY_GATE_ENABLED_AFTER } from "config/policy";

import {
  PolicyOverlay,
  PolicyCard,
  PolicyCardHeader,
  PolicyCardBody,
  PolicyBodyText,
  PolicySummary,
  DownloadLink,
  PolicyCardFooter,
  EffectiveDate,
  PolicyLogo,
  PolicyTitle,
} from "./styled";

const PROVIDER_SUMMARY = () => (
  <>
    <h4>Invoice Submission &amp; Accuracy</h4>
    <p>
      Invoices must be submitted via the Provider Portal or emailed to invoices@getmulch.com
      and must exactly match the accepted Purchase Order value.
    </p>
    <h4>Purchase Order Acceptance &amp; Payment Timing</h4>
    <p>
      By accepting a PO you accept the scope, pricing, and payment timing. Provider payments
      are issued after client funds are received, typically within 5 business days of client payment.
    </p>
    <h4>Provider Portal &amp; Photo Requirements</h4>
    <p>
      Clear before-photos and after-photos are required for project verification and payment processing.
    </p>
    <h4>Self-Performance, Insurance &amp; Communication</h4>
    <ul>
      <li>Work must be self-performed unless GetMulch.com approves subcontracting in writing</li>
      <li>Current, valid insurance documentation must be maintained on file at all times</li>
      <li>Communicate service dates clearly and notify GetMulch.com promptly of any delays</li>
    </ul>
  </>
);

const CLIENT_SUMMARY = () => (
  <>
    <h4>Payment Terms &amp; Enforcement</h4>
    <p>
      Payment must be received within the terms stated on each invoice. Approved methods include
      ACH, check, and credit card. Late fees of 1.5% per month may apply for overdue balances.
    </p>
    <h4>Early &amp; Upfront Payment Incentives</h4>
    <ul>
      <li>3% discount when paying 50% upfront and 50% upon completion</li>
      <li>8% discount when paying 100% upfront</li>
      <li>Incentives available within 5 days of quote acceptance</li>
    </ul>
    <h4>Pricing, Scheduling &amp; Scope</h4>
    <ul>
      <li>Projects under 20 cubic yards may be subject to increased pricing</li>
      <li>Expedited projects (within 7 days) may require a 75% deposit or full payment upfront</li>
      <li>All scope changes require a formal change order</li>
    </ul>
    <h4>Quotes &amp; Platform Usage</h4>
    <p>
      Quotes expire after 30 days and may require updated pricing. Clients are responsible for
      reviewing quotes, confirming project details, and approving change orders within the platform.
    </p>
  </>
);

/**
 * Check if the policy gate is active based on the configured date.
 * Returns false if POLICY_GATE_ENABLED_AFTER is set and we haven't reached that date yet.
 */
const isGateActive = (): boolean => {
  if (!POLICY_GATE_ENABLED_AFTER) return true; // null = enforce immediately
  return new Date() >= new Date(POLICY_GATE_ENABLED_AFTER);
};

interface PolicyGateProps {
  user: any;
  children: React.ReactNode;
}

const PolicyGate = ({ user, children }: PolicyGateProps) => {
  const [checking, setChecking] = useState(true);
  const [accepted, setAccepted] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  const accessLevel = user?.accessLevel;
  const gateActive = isGateActive();
  const requiresPolicy = gateActive && POLICY_REQUIRED_ACCESS_LEVELS.includes(accessLevel);
  const policyConfig = requiresPolicy ? POLICY_CONFIG[accessLevel] : null;

  const checkPolicy = useCallback(async () => {
    if (!requiresPolicy || !policyConfig || !user?.id) {
      setAccepted(true);
      setChecking(false);
      return;
    }

    const res = await checkPolicyAcceptance({
      policyType: policyConfig.policyType,
      policyVersion: policyConfig.policyVersion,
    });

    setAccepted(!!res?.accepted);
    setChecking(false);
  }, [requiresPolicy, policyConfig, user?.id]);

  useEffect(() => {
    checkPolicy();
  }, [checkPolicy]);

  // Fetch signed download URL when gate is shown
  useEffect(() => {
    if (!checking && !accepted && policyConfig?.downloadPath) {
      getPolicyDownloadUrl(policyConfig.downloadPath).then((res: any) => {
        if (res?.success && res?.url) {
          setDownloadUrl(res.url);
        }
      });
    }
  }, [checking, accepted, policyConfig]);

  const handleAccept = async () => {
    if (!policyConfig || !agreed) return;
    setSubmitting(true);

    const res = await acceptPolicy({
      policyType: policyConfig.policyType,
      policyVersion: policyConfig.policyVersion,
    });

    if (res?.success) {
      setAccepted(true);
    }
    setSubmitting(false);
  };

  // Gate not active yet, or internal users, or already accepted — pass through
  if (!requiresPolicy || accepted) {
    return <>{children}</>;
  }

  // Still checking — show nothing (avoids flash)
  if (checking) {
    return (
      <PolicyOverlay>
        <Spinner size={24} />
      </PolicyOverlay>
    );
  }

  const isProvider = accessLevel === "isProvider";

  return (
    <PolicyOverlay>
      <PolicyCard>
        <PolicyCardHeader>
          <PolicyLogo src="/images/logo-dark.png" alt={process.env.REACT_APP_SERVICE || "GetMulch"} />
          <PolicyTitle>{policyConfig?.title}</PolicyTitle>
          <EffectiveDate>Effective {policyConfig?.effectiveDate}</EffectiveDate>
        </PolicyCardHeader>

        <PolicyCardBody>
          <PolicyBodyText>
            Please review the key terms below and download the full policy document for your records.
            You must accept these terms to continue using the platform.
          </PolicyBodyText>

          <PolicySummary>
            {isProvider ? <PROVIDER_SUMMARY /> : <CLIENT_SUMMARY />}
          </PolicySummary>

          {downloadUrl && (
            <DownloadLink href={downloadUrl} target="_blank" rel="noopener noreferrer">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
              </svg>
              Download Full Policy (PDF)
            </DownloadLink>
          )}
        </PolicyCardBody>

        <PolicyCardFooter>
          <Checkbox
            label={`I have read and agree to the ${policyConfig?.title} effective ${policyConfig?.effectiveDate}`}
            checked={agreed}
            onChange={(val: any) => setAgreed(val)}
            labelSize="13px"
            $customStyles={{ alignItems: "flex-start" }}
          />
          <BasicButton
            type="button"
            scheme="success"
            disabled={!agreed}
            $submitting={submitting}
            $loadingColor="#fff"
            keepText={true}
            styles={{ width: "100%" }}
            onClick={handleAccept}
          >
            Accept &amp; Continue
          </BasicButton>
        </PolicyCardFooter>
      </PolicyCard>
    </PolicyOverlay>
  );
};

export default PolicyGate;
